import dynamic from "next/dynamic";
import Head from "next/head";

import { CSSProperties, ReactNode, useEffect, useState } from "react";

import RegulatoryQuarterCheck from "components/InsightProviderAgreement/RegulatoryQuarterlyCheck";
import PlatformSwitchBanner from "components/PlatformSwitchBanner";
import usePlatformSwitchBanner, {
  PLATFORM_SWITCH_BANNER_HEIGHT,
} from "components/PlatformSwitchBanner/usePlatformSwitchBanner";
import TopNavBar from "components/TopNavBar";
import useAuthentication from "hooks/useAuthentication";
import usePushNotifications from "hooks/usePushNotifications";
import useTheme from "hooks/useTheme";
import parseCookie from "utils/parseCookie";
import {
  setUiVersionCookie,
  UI_VERSION_COOKIE,
} from "utils/setUiVersionCookie";

const AskWidget = dynamic(() => import("components/AskWidget"), {
  ssr: false,
});

const Footer = dynamic(() => import("components/Footer"));
const MessagingDock = dynamic(() => import("components/MessagingDock"), {
  ssr: false,
});

type LayoutProps = {
  children: ReactNode;
  theme?: string;
};

const Layout = ({ children }: LayoutProps) => {
  const { authenticated, account } = useAuthentication();
  const [notificationPermission, setNotificationPermission] = useState<any>();

  const { requestPushNotificationToken } = usePushNotifications();
  const theme = useTheme();

  const { visible: showPlatformSwitchBanner, switchToNewUi } =
    usePlatformSwitchBanner();

  useEffect(() => {
    if (notificationPermission === "granted") {
      requestPushNotificationToken();
    }
  }, [notificationPermission, requestPushNotificationToken]);

  // Move internal users to the new UI, then reload so the page is
  // requested again with the new cookie
  useEffect(() => {
    const cookies = parseCookie(document.cookie);

    if (account?.isInternalUser && !cookies[UI_VERSION_COOKIE]) {
      setUiVersionCookie("new");
      window.location.href = "/login";
    }
  }, [account?.id, account?.isInternalUser]);

  useEffect(() => {
    if ("Notification" in window) {
      setNotificationPermission(Notification.permission);
    }
  }, []);

  return (
    <>
      <Head>
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black" />
        <meta name="theme-color" content="#40b187" />
        <meta name="msapplication-TileColor" content="#414141" />
        <meta
          name="msapplication-TileImage"
          content="https://assets.smartkarma.com/v2/img/apple-touch-icon.png"
        />
        <meta property="og:site_name" content="Smartkarma" />
        <meta name="twitter:site" content="@smartkarma" />
      </Head>
      <div
        className={`${theme}-theme`}
        style={
          showPlatformSwitchBanner
            ? ({
                "--sk-platform-switch-banner-height": `${PLATFORM_SWITCH_BANNER_HEIGHT}px`,
                paddingTop: PLATFORM_SWITCH_BANNER_HEIGHT,
              } as CSSProperties)
            : undefined
        }
      >
        {showPlatformSwitchBanner && (
          <PlatformSwitchBanner onSwitch={switchToNewUi} />
        )}
        <TopNavBar />
        {children}
        {!authenticated && <Footer />}
        {authenticated && <MessagingDock />}
        {authenticated && account?.canUseAsk && <AskWidget />}
        {authenticated && <RegulatoryQuarterCheck />}
      </div>
    </>
  );
};

export default Layout;
